import React, { Fragment, useEffect, useState } from "react";
import CardLink from "features/shared/components/buttons/CardLink";
import DistRateCircuitBreakerFormSection from "./distRateCircuitBreakerForm/DistRateCircuitBreakerFormSection";
import ReservedCapacityFormSection from "./reservedCapacityForm/ReservedCapacityFormSection";
import DataQuery from "data/DataQuery";
import { Customer_ContractDropdown } from "data/endpoints";
import { getRouteParams } from "features/shared/helpers/routeParams";
import { useSelector } from "react-redux";
import IAppState from "infrastructure/store/IAppState";
import CustomerState from "infrastructure/store/customer/CustomerState";
import { ContractStatus } from "features/contracts/enums/contractStatus";
import { ContractType } from "features/contracts/enums/contractType";
import { useQuery } from "data/hooks/useQuery";
import ContractDropdownDTO from "features/contracts/dtos/ContractDropdownDTO";
import { getQueryParams } from "features/shared/helpers/queryParams";
import Alert from "features/shared/alerts/components/Alert";
import { useTranslation } from "react-i18next";

type SectionType = "Signpost" | "ReservedCapacity" | "DistRateCircuitBreaker";

const CapacityRateCircuitBreakerMainSection = () => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region  state
  const [sectionType, setSectionType] = useState<SectionType>();
  //#endregion state

  //#region store
  const customer = (useSelector<IAppState, CustomerState>((store) => store.customer)).customer;
  //#endregion store

  //#region fetch data
  const getContractQuery = (contractType: ContractType): DataQuery => {
    let queryParams = getQueryParams("ContractType", [contractType]);
    queryParams.append("ContractStatus", ContractStatus.Effective);
    if (contractType === "ElectricityHighVoltage") {
      queryParams.append("ContractStatus", ContractStatus.Valid);
    }

    return {
      url: Customer_ContractDropdown,
      routeParams: getRouteParams("customerId", customer.customerId!),
      params: queryParams
    };
  }

  const { data: lowWoltageContracts, loadStatus: lowWoltageStatus } = useQuery<ContractDropdownDTO[]>(getContractQuery("ElectricityLowVoltage"), [], [customer]);
  const { data: highWoltageContracts, loadStatus: highWoltageStatus } = useQuery<ContractDropdownDTO[]>(getContractQuery("ElectricityHighVoltage"), [], [customer]);
  //#endregion fetch data

  //#region helpers
  const setCorrectSectionType = () => {
    if (isLoaded() && hasData()) {
      if (lowWoltageContracts.length > 0 && highWoltageContracts.length > 0) {
        setSectionType("Signpost");
      } else if (lowWoltageContracts.length > 0) {
        setSectionType("DistRateCircuitBreaker");
      } else if (highWoltageContracts.length > 0) {
        setSectionType("ReservedCapacity");
      }
    }
  }

  const hasData = (): boolean => lowWoltageContracts.length > 0 ||  highWoltageContracts.length > 0;

  const isLoaded = (): boolean => lowWoltageStatus === "AfterFirstLoad" && highWoltageStatus === "AfterFirstLoad";
  //#endregion helpers

  //#region  side-effects
  useEffect(() => {
    setCorrectSectionType();
  }, [lowWoltageStatus, highWoltageStatus])
  //#endregion  side-effects
  

  //#region  sub-renders
  const renderSection = () => {
    switch (sectionType) {
      case "Signpost":
        return renderSignpost();
      case "DistRateCircuitBreaker":
        return <DistRateCircuitBreakerFormSection contracts={lowWoltageContracts} />;
      case "ReservedCapacity":
        return <ReservedCapacityFormSection contracts={highWoltageContracts} />;
      default:
        return <Fragment />;
    }
  }

  const renderSignpost = () => {
    return (
      <div className="row no-gutters mx-n2">
        <div className="col-lg-6 px-2">
          <CardLink
            title={tLabels("capacityRateCircuitBreakerMainSection.changeDistributionRateCircuitBreaker")}
            onClick={() => setSectionType("DistRateCircuitBreaker")}
          />
        </div>
        <div className="col-lg-6 px-2">
          <CardLink
            title={tLabels("capacityRateCircuitBreakerMainSection.changeReservedCapacity")}
            onClick={() => setSectionType("ReservedCapacity")}
          />
        </div>
      </div>
    );
  };
  //#endregion  sub-renders

  //#region  render
  if (hasData()) {
    return <>{renderSection()}</>
  } else if (isLoaded()) {
    return <Alert alertObj={{ alertType: "Info", message: tLabels("capacityRateCircuitBreakerMainSection.dataNotFound")}} />;
  } else {
    return <></>
  }
  //#endregion render
};

export default CapacityRateCircuitBreakerMainSection;
